import { ThemeProvider } from "@/providers/ThemeProvider";
import { QueryProvider } from "@/providers/QueryProvider";
import { CookieConsentProvider } from "@/providers/CookieConsentProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { DataModeProvider } from "@/contexts/DataModeContext";
import { ETDProvider } from "@/contexts/ETDContext";
import { ErrorProvider } from "@/contexts/ErrorContext";
import { ErrorBoundary } from "@/components/error";
import { UserPreferencesProvider } from "@/providers/UserPreferencesProvider";
import { WootricProvider } from "@/providers/WootricProvider";
import { FeedbackConfigProvider } from "@/providers/FeedbackConfigProvider";
import { useUserProfile } from "@/hooks/useUserProfile";
import { AppWithProviders } from "@/App";
import etdProductData from "@/data/etd-product-data.json";
import { WorkspaceSettingsProvider, type WorkspaceSettings } from "@/contexts/WorkspaceSettingsContext";
import { SyncNotificationProvider } from "@/components/integrations/SyncNotificationProvider";
import type { WootricDomain } from "@/types/wootric";

interface AppContainerProps {
  settings?: {
    workspaceUrl?: string;
    defaultUrl?: string;
    wootricDomain?: WootricDomain;
    sentimentAccountToken?: string;
    sentimentApiHost?: string;
    sentimentReDisplayAfterDays?: number;
    sentimentMfeVersion?: string;
    mfeRegistryBase?: string;
  } & Record<string, unknown>;
}

function AppContent({ wootricDomain }: { wootricDomain?: WootricDomain }) {
  const { user: wootricUser } = useUserProfile();

  return (
    <WootricProvider user={wootricUser} wootricDomainOverride={wootricDomain}>
      <SyncNotificationProvider>
        <Toaster richColors duration={5000} closeButton />
        <AppWithProviders />
      </SyncNotificationProvider>
    </WootricProvider>
  );
}

export default function AppContainer(props: AppContainerProps) {
  const workspaceSettings: WorkspaceSettings = {
    workspaceUrl: props.settings?.workspaceUrl,
    defaultUrl: props.settings?.defaultUrl,
  };

  return (
    <FeedbackConfigProvider
      accountToken={props.settings?.sentimentAccountToken}
      apiHost={props.settings?.sentimentApiHost}
      reDisplayAfterDays={props.settings?.sentimentReDisplayAfterDays}
      mfeRegistryBase={props.settings?.mfeRegistryBase}
      mfeVersion={props.settings?.sentimentMfeVersion}
    >
      <ErrorBoundary>
        <QueryProvider>
          <CookieConsentProvider>
            <ErrorProvider>
              <ETDProvider fallbackProductData={etdProductData}>
                <DataModeProvider>
                  <WorkspaceSettingsProvider settings={workspaceSettings}>
                    <UserPreferencesProvider>
                      <TooltipProvider>
                        <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} disableTransitionOnChange>
                          <AppContent wootricDomain={props.settings?.wootricDomain} />
                        </ThemeProvider>
                      </TooltipProvider>
                    </UserPreferencesProvider>
                  </WorkspaceSettingsProvider>
                </DataModeProvider>
              </ETDProvider>
            </ErrorProvider>
          </CookieConsentProvider>
        </QueryProvider>
      </ErrorBoundary>
    </FeedbackConfigProvider>
  );
}
